/**
 * Temporary opening-hand pins for the current ruling-audit step.
 *
 * Replace `OPENING_HAND_TEST_IDS` when starting the next step (do not accumulate).
 * Leave the array empty to restore the normal random deal.
 */
export const OPENING_HAND_TEST_IDS: string[] = []

/**
 * Optional override for seat 1. When empty, both seats use OPENING_HAND_TEST_IDS.
 * Use this when the two sides need different cards (e.g. step 3: your Sword,
 * their Warrior).
 */
export const OPENING_HAND_TEST_IDS_SEAT_1: string[] = []

/** How many copies of each seed card to plant in the 40-card deck. */
export const TEST_SEED_COPIES = 3;

const DECK_SIZE = 40;

/**
 * Which seed IDs a given seat should see in its opening hand / deck.
 */
export function seedIdsForSeat(seat: 0 | 1): readonly string[] {
  if (seat === 1 && OPENING_HAND_TEST_IDS_SEAT_1.length > 0) {
    return OPENING_HAND_TEST_IDS_SEAT_1;
  }
  return OPENING_HAND_TEST_IDS;
}

/**
 * All distinct seed IDs that must exist in generated decks (union of both seats).
 */
export function allSeedCardIds(): string[] {
  const ids = new Set<string>(OPENING_HAND_TEST_IDS);
  for (const id of OPENING_HAND_TEST_IDS_SEAT_1) ids.add(id);
  return [...ids];
}

/**
 * Plants `copies` of each seed card into a generated deck, dropping extras so
 * the list stays at 40. No-op when the seed list is empty. Extra Deck monsters
 * are skipped — they must not be injected into the Main Deck.
 *
 * @param deck - The generated main deck
 * @param findCard - Catalog lookup used when the random draw missed a seed
 * @param copies - Copies per seed (default 3, the official maximum)
 */
export function seedTestCardsIntoDeck<T extends { id: string | number }>(
  deck: T[],
  findCard: (id: string) => T | undefined,
  copies: number = TEST_SEED_COPIES,
  seat?: 0 | 1,
): T[] {
  const ids = seat === 0 || seat === 1 ? [...seedIdsForSeat(seat)] : allSeedCardIds();
  if (ids.length === 0) return deck;
  const result = deck.slice();
  for (const id of ids) {
    const template = findCard(String(id)) ?? findCard(id);
    if (!template) continue;
    if (isFusionLike(template)) continue;
    const keptFoils = result
      .filter((card) => String(card.id) === id)
      .map((card) => (card as { foil?: string }).foil);
    for (let i = result.length - 1; i >= 0; i--) {
      if (String(result[i].id) === id) result.splice(i, 1);
    }
    for (let n = 0; n < copies; n++) {
      if (result.length >= DECK_SIZE) result.pop();
      const foil = keptFoils[n];
      result.unshift(
        foil ? ({ ...template, foil } as T) : ({ ...template } as T),
      );
    }
  }
  return result;
}

function isFusionLike(card: object): boolean {
  const rec = card as {
    type?: string;
    typeline?: string[];
    humanReadableCardType?: string;
    frameType?: string;
  };
  if (rec.frameType === 'fusion') return true;
  if (rec.typeline?.some((t) => t.toLowerCase() === 'fusion')) return true;
  const blob = `${rec.type ?? ''} ${rec.humanReadableCardType ?? ''}`.toLowerCase();
  return blob.includes('fusion');
}

/**
 * Moves one copy of each seed id to the front of the deck so the opening deal
 * draws them. Mutates `deck` in place. Missing ids are skipped.
 */
export function pinOpeningHand<T extends { cardId: string }>(
  deck: T[],
  ids: readonly string[],
  makeMissing?: (id: string) => T | undefined,
): void {
  if (ids.length === 0) return;
  const pinned: T[] = [];
  for (const id of ids) {
    const index = deck.findIndex((card) => String(card.cardId) === String(id));
    if (index !== -1) {
      pinned.push(deck.splice(index, 1)[0]!);
      continue;
    }
    const created = makeMissing?.(id);
    if (created) {
      if (deck.length > 0) deck.pop();
      pinned.push(created);
    }
  }
  deck.unshift(...pinned);
}
